import React, { useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { SITE_URL } from '../../constants/site';
import { createLogger } from '../../utils/logger';

const logger = createLogger('ShareButton');

/**
 * ShareButton - Social sharing for products and supermarkets
 * Uses Web Share API when available (mobile), with dropdown for platform-specific share links
 * @param {Object} props
 * @param {string} props.url - Full URL to share (defaults to current page)
 * @param {string} props.title - Title for share (e.g. product/supermarket name)
 * @param {string} props.text - Optional description text
 * @param {string} props.variant - Bootstrap button variant
 * @param {string} props.size - Bootstrap button size
 * @param {boolean} props.showLabel - Show "Share" label
 * @param {string} props.className - Additional CSS classes
 */
const ShareButton = ({
  url,
  title,
  text = '',
  variant = 'outline-secondary',
  size = 'sm',
  showLabel = true,
  className = '',
}) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : SITE_URL);
  const shareTitle = title || 'Pack My Cart';
  const shareText = text || `Check this out on Pack My Cart`;

  const handleWebShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          logger.warn('Share failed:', err);
        }
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.warn('Copy failed:', err);
    }
  };

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(shareTitle);
  const encodedText = encodeURIComponent(shareText);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`,
  };

  const hasWebShare = typeof navigator !== 'undefined' && navigator.share;

  return (
    <Dropdown align="end">
      <Dropdown.Toggle
        as={Button}
        variant={variant}
        size={size}
        className={className}
        id="share-dropdown"
        aria-label="Share"
      >
        <i className="fas fa-share-alt me-1" aria-hidden="true" />
        {showLabel && 'Share'}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {hasWebShare && (
          <>
            <Dropdown.Item onClick={handleWebShare}>
              <i className="fas fa-mobile-alt me-2" aria-hidden="true" />
              Share via...
            </Dropdown.Item>
            <Dropdown.Divider />
          </>
        )}
        <Dropdown.Item href={shareLinks.facebook} target="_blank" rel="noopener noreferrer">
          <i className="fab fa-facebook me-2 text-primary" aria-hidden="true" />
          Facebook
        </Dropdown.Item>
        <Dropdown.Item href={shareLinks.twitter} target="_blank" rel="noopener noreferrer">
          <i className="fab fa-twitter me-2 text-info" aria-hidden="true" />
          X (Twitter)
        </Dropdown.Item>
        <Dropdown.Item href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer">
          <i className="fab fa-whatsapp me-2 text-success" aria-hidden="true" />
          WhatsApp
        </Dropdown.Item>
        <Dropdown.Item href={shareLinks.email}>
          <i className="fas fa-envelope me-2" aria-hidden="true" />
          Email
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item onClick={handleCopyLink}>
          <i className="fas fa-link me-2" aria-hidden="true" />
          {copied ? 'Copied!' : 'Copy link'}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ShareButton;
